import { Alert, Grow } from "@mui/material";
import * as React from "react";

import {
  Notification,
  NotificationDispatchContext,
  NotificationSeverity,
  ShowNotification,
} from "./NotificationContext";
import styles from "./NotificationProvider.module.css";

function NotificationItem({
  notification,
  onDismiss,
}: {
  notification: Notification;
  onDismiss: (id: string) => void;
}) {
  const [exiting, setExiting] = React.useState(false);

  const handleDismiss = React.useCallback(() => {
    setExiting(true);
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(handleDismiss, notification.duration);
    return () => clearTimeout(timer);
  }, [notification.duration, handleDismiss]);

  return (
    <Grow
      in={!exiting}
      unmountOnExit
      onExited={() => onDismiss(notification.id)}
    >
      <Alert
        severity={notification.severity}
        variant="filled"
        onClose={handleDismiss}
        className={styles.alert}
      >
        {notification.message}
      </Alert>
    </Grow>
  );
}

function NotificationDisplay({
  notifications,
  onDismiss,
}: {
  notifications: Notification[];
  onDismiss: (id: string) => void;
}) {
  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      {notifications.map((n) => (
        <NotificationItem key={n.id} notification={n} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

export function NotificationProvider({ children }: React.PropsWithChildren) {
  const [notifications, setNotifications] = React.useState<Notification[]>([]);

  const showNotification = React.useCallback<ShowNotification>(
    (message, severity: NotificationSeverity = "success", duration = 3000) => {
      setNotifications((prev) => [
        ...prev,
        { id: crypto.randomUUID(), message, severity, duration },
      ]);
    },
    [],
  );

  const dismiss = React.useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return (
    <NotificationDispatchContext.Provider value={showNotification}>
      {children}
      <NotificationDisplay notifications={notifications} onDismiss={dismiss} />
    </NotificationDispatchContext.Provider>
  );
}
